import axios, {
  AxiosError,
  AxiosPromise,
  AxiosRequestConfig,
  AxiosResponse,
  CancelTokenSource,
} from "axios";
import get from "lodash/get";
import { CANCEL } from "redux-saga";

import { STATUS_CODE_CANCEL } from "cortex/ui/src/constant/app";
import { auth } from "cortex/ui/src/store/auth";
import * as QS from "cortex/ui/src/util/queryString";

declare const process: {
  env: {
    NODE_ENV?: string;
  };
};

export const HEADER_AUTHORIZATION_TOKEN = "Authorization" as const;
export const HEADER_CONTENT_TYPE = "Content-Type" as const;

export type CancelableAxiosPromise<T = any> = AxiosPromise<T> &
  Partial<Record<typeof CANCEL, () => void>>;

export type DataType = "binary" | "form" | "json";

export interface GetOptions {
  cancelSource?: CancelTokenSource;
  dataType?: DataType;
}

export type HeaderMap = Record<string, string>;

export interface S3PutSuccess<T = any> {
  data: T;
  message: string;
  success: true;
}

export class BaseService {
  delete<T = any>(
    url: string,
    params?: unknown,
    dataType: DataType = "json",
  ): CancelableAxiosPromise<T> {
    const data = JSON.stringify(params);

    return _makeCancelableRequest<T>({
      data,
      headers: _getHeaders(dataType),
      method: "DELETE",
      url,
    });
  }

  get<T = any>(
    url: string,
    params?: unknown,
    options: GetOptions = {},
  ): CancelableAxiosPromise<T> {
    const { cancelSource, dataType = "json" } = options;

    return _makeCancelableRequest<T>(
      {
        headers: _getHeaders(dataType),
        method: "GET",
        params,
        url,
      },
      cancelSource,
    );
  }

  post<T = any>(
    url: string,
    params?: unknown,
    dataType: DataType = "json",
    headers: HeaderMap | null = null,
  ): CancelableAxiosPromise<T> {
    const data = dataType === "json" ? JSON.stringify(params) : (params as any);

    return _makeCancelableRequest<T>({
      data,
      headers: headers ?? _getHeaders(dataType),
      method: "POST",
      url,
    });
  }

  async put<T = any>(
    url: string,
    params: unknown,
    dataType: DataType = "json",
    additionalHeaders: HeaderMap = {},
    isS3Upload = false,
  ): Promise<AxiosResponse<T> | S3PutSuccess<T>> {
    if (isS3Upload && !_isValidS3PresignedUrl(url)) {
      throw new Error("Invalid S3 presigned URL");
    }

    const data = dataType === "json" ? JSON.stringify(params) : (params as any);

    try {
      const headers = _getHeaders(
        dataType,
        params,
        isS3Upload,
        additionalHeaders,
      );

      const response = await _makeCancelableRequest<T>({
        data,
        headers,
        method: "PUT",
        url,
      });

      if (isS3Upload && response.status === 200) {
        return {
          data: response.data,
          message: "File uploaded successfully to S3",
          success: true,
        };
      }

      return response;
    } catch (error: any) {
      if (isS3Upload) {
        if (axios.isCancel(error)) {
          throw new Error("Upload canceled");
        }

        const axiosError = error as AxiosError;

        if (axiosError.response) {
          switch (axiosError.response.status) {
            case 400:
              throw new Error(
                "Bad request: Invalid presigned URL or mismatched headers",
              );
            case 403:
              throw new Error(
                "Permission denied: Check S3 presigned URL or bucket permissions",
              );
            case 404:
              throw new Error("S3 bucket or resource not found");
            default:
              throw new Error(`S3 upload failed: ${axiosError.message}`);
          }
        }

        // eslint-disable-next-line no-console
        console.error("S3 network error:", {
          config: (axiosError as any)?.config,
          message: axiosError?.message,
        });

        throw new Error(
          `Network error: ${axiosError?.message ?? String(error)}`,
        );
      }

      throw error;
    }
  }
}

export function _getHeaders(
  dataType: DataType = "json",
  params: unknown = null,
  isS3Upload = false,
  additionalHeaders: HeaderMap = {},
): HeaderMap {
  const headers: HeaderMap = {};

  if (!isS3Upload) {
    const token: string | null | undefined = auth.getToken();
    if (token) {
      headers[HEADER_AUTHORIZATION_TOKEN] = `Bearer ${token}`;
    }
  }

  if (dataType === "json") {
    headers[HEADER_CONTENT_TYPE] = "application/json";
  } else if (dataType === "form") {
    // Let axios set multipart boundaries
  } else if (
    isS3Upload &&
    typeof File !== "undefined" &&
    params instanceof File &&
    !additionalHeaders[HEADER_CONTENT_TYPE]
  ) {
    headers[HEADER_CONTENT_TYPE] = params.type || "application/octet-stream";
  } else {
    headers[HEADER_CONTENT_TYPE] = "application/octet-stream";
  }

  const mergedHeaders: HeaderMap = { ...headers, ...additionalHeaders };

  Object.keys(additionalHeaders).forEach((key) => {
    if (
      !key.toLowerCase().startsWith("x-amz-") &&
      key !== HEADER_CONTENT_TYPE
    ) {
      // eslint-disable-next-line no-console
      console.warn(`Unexpected header in additionalHeaders: ${key}`);
    }
  });

  return mergedHeaders;
}

export function _handleError(apiError: unknown): Promise<never> {
  if (axios.isCancel(apiError)) {
    const err = apiError as any;
    err.status = STATUS_CODE_CANCEL;
    return Promise.reject(err);
  }

  return Promise.reject(get(apiError as any, "response", {}));
}

export function _handleSuccess<T = any>(
  response: AxiosResponse<T>,
): AxiosResponse<T> {
  return response;
}

export function _isValidS3PresignedUrl(url: string): boolean {
  try {
    const parsedUrl = new URL(url);
    return (
      parsedUrl.hostname.endsWith("s3.amazonaws.com") ||
      parsedUrl.hostname.includes("s3-") ||
      parsedUrl.searchParams.has("X-Amz-Signature")
    );
  } catch {
    return false;
  }
}

export function _makeCancelableRequest<T = any>(
  config: AxiosRequestConfig,
  cancelSource?: CancelTokenSource,
): CancelableAxiosPromise<T> {
  const source = cancelSource ?? axios.CancelToken.source();

  const request = axios({
    ...config,
    cancelToken: source.token,
    paramsSerializer: QS.stringify as any,
  }) as CancelableAxiosPromise<T>;

  request[CANCEL] = () => {
    // eslint-disable-next-line no-console
    console.info("[saga] xhr cancellation request:", config.method, config.url);
    source.cancel();
  };

  return request;
}

// ---------------------------------------------------------------------------
// Dev-only API timing instrumentation
// Registers BEFORE the existing error-shaping interceptor so that the raw
// config (url, method, timestamps) is still accessible on both success and
// error paths.  Guards on NODE_ENV so these never run in production builds.
// ---------------------------------------------------------------------------
if (
  process.env.NODE_ENV === "development" ||
  process.env.NODE_ENV === "local"
) {
  interface _PageStats {
    calls: number;
    totalMs: number;
    startTime: number;
  }

  const _pageStats = new Map<string, _PageStats>();
  const _summaryTimers = new Map<string, ReturnType<typeof setTimeout>>();

  const _nowMs = (): number =>
    typeof performance !== "undefined" && typeof performance.now === "function"
      ? performance.now()
      : Date.now();

  const _apiLogPath = (url: unknown): string => {
    if (typeof url !== "string" || url.length === 0) {
      return "(unknown)";
    }

    try {
      return new URL(url, "http://api.local").pathname;
    } catch {
      const queryStart = url.search(/[?#]/);
      return queryStart === -1 ? url : url.slice(0, queryStart);
    }
  };

  const _schedulePageSummary = (page: string): void => {
    const existingTimer = _summaryTimers.get(page);
    if (existingTimer !== undefined) clearTimeout(existingTimer);

    const summaryTimer = setTimeout(() => {
      const stats = _pageStats.get(page);
      if (stats && stats.calls > 0) {
        // eslint-disable-next-line no-console
        console.log(
          `[API:SUMMARY] ${page} — ${stats.calls} call${stats.calls !== 1 ? "s" : ""}, ${Math.round(stats.totalMs)}ms total`,
        );
        _pageStats.delete(page);
      }
      _summaryTimers.delete(page);
    }, 1000);
    _summaryTimers.set(page, summaryTimer);
  };

  axios.interceptors.request.use((config) => {
    const page =
      typeof window !== "undefined" ? window.location.pathname : "(server)";
    (config as any).__apiTimingStart = _nowMs();
    (config as any).__apiTimingPage = page;

    if (!_pageStats.has(page)) {
      _pageStats.set(page, {
        calls: 0,
        totalMs: 0,
        startTime: _nowMs(),
      });
    }

    return config;
  });

  axios.interceptors.response.use(
    (response) => {
      const cfg = response.config as any;
      const start: number | undefined = cfg.__apiTimingStart;

      if (start !== undefined) {
        const duration = Math.round(_nowMs() - start);
        const url = _apiLogPath(cfg.url);
        const method = (cfg.method ?? "GET").toUpperCase();
        const status = response.status;
        const page: string =
          cfg.__apiTimingPage ??
          (typeof window !== "undefined"
            ? window.location.pathname
            : "(server)");

        // eslint-disable-next-line no-console
        console.log(`[API] ${method} ${url} → ${status} OK (${duration}ms)`);

        const stats = _pageStats.get(page);
        if (stats) {
          stats.calls++;
          stats.totalMs += duration;
          _schedulePageSummary(page);
        }
      }

      return response;
    },
    (error) => {
      if (!axios.isCancel(error)) {
        const cfg = error?.config as any;
        const start: number | undefined = cfg?.__apiTimingStart;

        if (start !== undefined) {
          const duration = Math.round(_nowMs() - start);
          const url = _apiLogPath(cfg.url);
          const method = (cfg.method ?? "GET").toUpperCase();
          const status: number | string = error?.response?.status ?? "ERR";
          const page: string =
            cfg.__apiTimingPage ??
            (typeof window !== "undefined"
              ? window.location.pathname
              : "(server)");

          // eslint-disable-next-line no-console
          console.log(
            `[API] ${method} ${url} → ${status} ERROR (${duration}ms)`,
          );

          const stats = _pageStats.get(page);
          if (stats) {
            stats.calls++;
            stats.totalMs += duration;
            _schedulePageSummary(page);
          }
        }
      }

      return Promise.reject(error);
    },
  );
}

axios.interceptors.response.use(_handleSuccess, _handleError);

export const baseService = new BaseService();
export default baseService;
